import React, { useEffect, useState } from 'react';
import { Button, Text, TextInput, View } from "../../Components/Themed";
import type { FocusPositionProps, PlayerReferralProps, PromoProps } from '../../types';
import { PartnerPortalApi, PartnerPortalHelpers } from '../api';
import { FlatList, ImageBackground, type ViewStyle } from 'react-native';
import PromoSelector from './PromoSelector';
import { useColors } from '../../constants/useColors';
import { showConfirmAlert } from '../../Components/ConfirmAlert';

type ReferralCodeManagerProps = {
    player_referral_id?:string,
    company_id?:string,
    onFocusPosition?:(pos:FocusPositionProps) => void,
    float?:boolean,
    onClose: () => void,
    header_style?:ViewStyle,
    footer_style?:ViewStyle
}
const sections = ['header','info','code','promo','inactivate','activate','delete']
const ReferralCodeManager = ({ float, player_referral_id, company_id, header_style, footer_style, onFocusPosition, onClose }:ReferralCodeManagerProps) => {
    const Colors = useColors();
    const [ action_loading, setActionLoading ] = useState(false);
    const [ size, setSize ] = useState({ height:0, width:0 });
    const [ state, setState ] = useState<{
        checked:boolean,
        available:boolean,
        promos:PromoProps[],
        player_referral?:PlayerReferralProps,
        draft_referral?:PlayerReferralProps
    }>({
        draft_referral: undefined,
        player_referral:undefined,
        promos:[],
        checked:false,
        available:false
    });
    const { draft_referral, promos, player_referral, checked, available } = state;
    const active_promo = promos.find(p => p.promo_id == draft_referral?.promo_id);
    useEffect(() => {
        PartnerPortalApi.setEnvironment();
        getData(player_referral_id);
    },[player_referral_id]);

    const getData = async(id?:string) => {
        const s_promos = await PartnerPortalApi.CompanyApi.getPromos();
        let s_refer:PlayerReferralProps | undefined
        if(id){
            s_refer = await PartnerPortalApi.CompanyApi.getReferralById(id);
            if(!s_refer){ return alert('Unable to get referral.  Please try again') }
        } else {
            s_refer = PartnerPortalHelpers.CompanyHelpers.generateNewReferral()
        }
        setState({
            checked:id ? true : false,
            available: id ? true : false,
            promos:s_promos,
            draft_referral: s_refer,
            player_referral: id ? s_refer : undefined
        })
    }

    const isValid = () => {
        let errors:string[] = []
        if(!draft_referral){ return ['Please complete this form'] }
        if(!draft_referral.referral_code){ errors.push('Please enter a valid code') }
        if(!checked){ errors.push('Please check if the referral code is available') }
        if(!available){ errors.push('This code is not available to use') }
        if(!active_promo){ errors.push('Add a promo code') }
        return errors
    }
    const errors = isValid();

    const checkAvailability = async(code:string) => {
        if(action_loading){ return }
        if(!code){ return alert('Please enter a code') }
        setActionLoading(true);
        const resp = await PartnerPortalApi.CompanyApi.getReferralByCode(code.toLowerCase());
        setState({ ...state, checked: true, available: resp.player_referral ? false : true });
        setActionLoading(false)
    }

    const deactivate = async() => {
        if(!player_referral){ return }
        if(action_loading){ return }
        setActionLoading(true);
        const new_code = await PartnerPortalApi.CompanyApi.deactivateCode(player_referral.player_referral_id);
        if(!new_code){ return alert('Unable to process. Please try again later') }
        setState({ ...state, player_referral: new_code, draft_referral:new_code })
        setActionLoading(false);
    }

    const activate = async() => {
        if(!player_referral){ return }
        if(action_loading){ return }
        setActionLoading(true);
        const new_code = await PartnerPortalApi.CompanyApi.activateCode(player_referral.player_referral_id);
        if(!new_code){ return alert('Unable to process. Please try again later') }
        setState({ ...state, player_referral: new_code, draft_referral:new_code })
        setActionLoading(false);
    }

    const deleteCode = async() => {
        if(!player_referral){ return }
        if(action_loading){ return }
        setActionLoading(true);
        const new_code = await PartnerPortalApi.CompanyApi.deleteCode(player_referral.player_referral_id);
        if(!new_code){ return alert('Unable to process. Please try again later') }
        setState({ ...state, player_referral: new_code, draft_referral:new_code })
        setActionLoading(false);
    }

    const handleUpdate = async(dr:PlayerReferralProps) => {
        if(action_loading){ return }
        setActionLoading(true);
        const new_referral = await PartnerPortalApi.CompanyApi.updateCodeOnReferral(dr);
        if(!new_referral){ return alert('Unable to process at this time') }
        setState({
            ...state,
            draft_referral: new_referral,
            player_referral: new_referral
        });
        setActionLoading(false);
    }

    const handleCreate = async() => {
        if(action_loading){ return }
        if(!draft_referral){ return }
        if(errors.length > 0){ return alert(errors.map(e => e)) }
        setActionLoading(true);
        const new_code = await PartnerPortalApi.CompanyApi.createCode({ ...draft_referral, company_id});
        if(!new_code){ return alert('Unable to process. Please try again later') }
        setState({ ...state, player_referral: new_code, draft_referral:new_code })
        setActionLoading(false);
    }

    const renderSections = (data: { item:string, index:number }) => {
        switch(data.item){
            case 'header':
                return (
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, ...header_style }}>
                      <View transparent style={{ flex:1 }}>
                        <Text theme='h1'>Manage Referral Code</Text>
                        <Text theme='description' style={{ marginTop:3 }}>Update or create your referral code below</Text>
                      </View>
                    </View>
                )
            case 'info':
                return (
                    <ImageBackground
                        source={{ uri: 'https://res.cloudinary.com/hoabts6mc/image/upload/w_1000,ar_16:9,c_fill,g_auto,e_sharpen/v1758727070/ChatGPT_Image_Sep_24_2025_10_17_44_AM_w39gdn.png' }}
                        style={{ width: size.width, height:225, justifyContent:'flex-end', overflow:'hidden', ...header_style}}>
                        <View type='dark_blur' style={{ padding:20 }}>
                            <Text theme='h1' size={36}>Referral Code</Text>
                            <Text theme='description' size={16} style={{ marginTop:3 }}>Track the users that sign up for BettorEdge with referral codes</Text>
                        </View>
                    </ImageBackground>
                )
            case 'code':
                if(!draft_referral){ return <></> }
                return (
                    <View transparent style={{ padding:20, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                        <View transparent style={{ marginBottom:10 }}>
                            <Text theme='h1'>Referral Code</Text>
                            <Text theme='description' style={{ marginTop:4 }}>Enter the referral code and check its availability</Text>
                        </View>
                        <View transparent type='row'>
                            <TextInput
                                value={draft_referral.referral_code}
                                onFocusPosition={onFocusPosition}
                                editable={player_referral ? false : true}
                                style={{ flex:1, marginLeft:5, textAlign:'center', borderColor:available?Colors.text.success:checked?Colors.text.error:Colors.text.warning }}
                                onChangeText={(text) => setState({ ...state, checked:false, available: false, draft_referral: { ...draft_referral, referral_code: text } })}
                            />
                            {!player_referral ?
                            <Button
                                title='CHECK'
                                type='success'
                                loading={action_loading}
                                disabled={checked || !draft_referral.referral_code}
                                style={{ marginLeft:5, opacity: checked || !draft_referral.referral_code ? 0.5 : 1 }}
                                onPress={() => checkAvailability(draft_referral.referral_code)}
                            />
                            :<></>}
                        </View>
                        {player_referral ?
                        <Text theme='error' style={{ marginTop:4 }}>To change the referral code.  Please inactivate this one and create a new one</Text>
                        :checked && available?
                        <Text theme='success' style={{ marginTop:4 }}>Great! This code is available</Text>
                        :checked && !available ?
                        <Text theme='error' style={{ marginTop:4 }}>Sorry! This code is already taken</Text>
                        :<></>}
                    </View>
                )
            case 'promo':
                if(!draft_referral){ return <></> }
                return (
                    <View transparent style={{ padding:20 }}>
                        <View transparent style={{ marginBottom:10 }}>
                            <Text theme='h1'>Promotion Type</Text>
                            <Text theme='description' style={{ marginTop:4 }}>Select the type of promotion for this code</Text>
                        </View>
                        <PromoSelector 
                            promos={promos}
                            limited_type='promo_code'
                            max_value={20}
                            active_promo={active_promo}
                            onSelectPromo={(promo) => setState({ ...state, draft_referral: { ...draft_referral, promo_id: promo.promo_id } })}
                        />
                    </View>
                )
            case 'inactivate':
                if(!player_referral){ return <></> }
                if(player_referral.status != 'pending'){ return <></> }
                
                return (
                    <View transparent style={{ padding:20 }}>
                        <Button
                            title='DEACTIVATE CODE'
                            loading={action_loading}
                            type='error'
                            onPress={() => deactivate()}
                        />
                    </View>
                )
            case 'activate':
                if(!player_referral){ return <></> }
                if(player_referral.status != 'inactive'){ return <></> }
                return (
                    <View transparent style={{ flexDirection:'row', padding:20 }}>
                        <Button
                            title='ARCHVE'
                            loading={action_loading}
                            type='error'
                            style={{ flex:1, marginRight:5 }}
                            onPress={() => {
                                showConfirmAlert('Are You Sure?', 'Once archived the code can no longer be activated or used',
                                    () => deleteCode(),
                                    () => console.log('')
                                )
                            }}
                        />
                        <Button
                            title='ACTIVATE CODE'
                            loading={action_loading}
                            type='success'
                            style={{ flex:3 }}
                            onPress={() => activate()}
                        />
                    </View>
                )
            default: return <></>
        }
    }
    const is_changed = JSON.stringify(player_referral) != JSON.stringify(draft_referral) ? true : false
    return (
        <View float={float} style={{ flex:1 }} onLayout={(ev) => {
            const { width, height } = ev.nativeEvent.layout
            setSize({ width, height })
        }}>
            <FlatList
                data={sections}
                key='code_sections'
                keyExtractor={item => item}
                renderItem={renderSections}
            />
            {player_referral?.status == 'closed' ?
             <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:'center', alignItems:'center' }}>
                <View float style={{ height:200, width:200, justifyContent:'center', alignItems:'center' }}>
                    <Text theme='h1' textAlign='center'>CODE HAS BEEN ARCHIVED</Text>
                </View>
            </View>
            :<></>}
            <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10, ...footer_style }}>
              <Button
                title='CLOSE'
                type='close'
                style={{ flex:1 }}
                onPress={() => onClose()}
              />
              {player_referral && draft_referral ?
              <Button
                title='SAVE CODE'
                style={{ flex:3, marginLeft:5, opacity: is_changed && !action_loading ? 1 : 0.5 }}
                loading={action_loading}
                type='success'
                disabled={!is_changed || action_loading}
                onPress={() => handleUpdate(draft_referral)}
              />
              :
                <Button
                title='CREATE CODE'
                style={{ flex:3, marginLeft:5, opacity: errors.length == 0 && !action_loading ? 1 : 0.5 }}
                loading={action_loading}
                type='success'
                disabled={action_loading}
                onPress={() => handleCreate()}
              />
              }
            </View>
        </View>
    )
}
export default ReferralCodeManager